import UntitledIcon from '@payaca/untitled-icons';
import { Button, H4, Modal, Text } from '@payaca/ambrosia';
import {
  type SolarElectricalTopology,
  type SolarIdentifiedPanel,
} from '@payaca/helpers/solarElectricalTopology';
import {
  buildSolarSingleLineDiagram,
  solarDiagramPaint,
  solarSingleLineDiagramSvg,
  type SolarDiagramProduct,
  type SolarDiagramRegion,
  type SolarDiagramShape,
  type SolarDiagramTone,
} from '@payaca/helpers/solarSingleLineDiagram';
import { type SolarRecommendedApplianceFieldsFragment } from '@/gql/graphql';
import { useMemo, useState } from 'react';
import { compassLabel } from './roofLabels';

type Props = {
  region: SolarDiagramRegion;
  topology: SolarElectricalTopology;
  panels: SolarIdentifiedPanel[];
  /** Roofs in picker order, numbered as in the string list. */
  faces: { id: number; azimuthDegrees: number }[];
  excludedFaceIds: number[];
  /** Each string's swatch, as the map and Strings list draw it. */
  stringColours: ReadonlyMap<string, string>;
  appliances: SolarRecommendedApplianceFieldsFragment[];
  panelAppliances: SolarRecommendedApplianceFieldsFragment[];
  /**
   * The design's selected battery: DC on a hybrid unit when it has no inverter
   * of its own, AC when it has one, otherwise marked "coupling unverified".
   */
  battery?: SolarRecommendedApplianceFieldsFragment | null;
  /** Site design low (°C) for the string voltage badges. */
  siteMinTemperatureC?: number | null;
};

const product = ({
  appliance,
}: SolarRecommendedApplianceFieldsFragment): SolarDiagramProduct => ({
  ...appliance.productSpec,
  applianceId: appliance.id,
});

const TONE_CLASS: Record<SolarDiagramTone, string | undefined> = {
  default: undefined,
  muted: 'text-gray-500',
  ok: 'text-green-700',
  warn: 'text-yellow-700',
};

const shapeElement = (shape: SolarDiagramShape, key: number) => {
  if (shape.type === 'text')
    return (
      <text
        key={key}
        x={shape.x}
        y={shape.y}
        fontSize={shape.size}
        fontWeight={shape.weight === 'bold' ? 600 : undefined}
        textAnchor={shape.anchor}
        fill="currentColor"
        className={TONE_CLASS[shape.tone ?? 'default']}
      >
        {shape.text}
      </text>
    );
  const paint = solarDiagramPaint(shape, () => 'currentColor');
  const common = {
    stroke: paint.stroke,
    fill: paint.fill,
    fillOpacity: paint.fillOpacity,
    strokeWidth: paint.strokeWidth,
    strokeDasharray: paint.strokeDasharray,
    className: TONE_CLASS[shape.tone ?? 'default'],
  };
  switch (shape.type) {
    case 'line':
      return (
        <line
          key={key}
          x1={shape.x1}
          y1={shape.y1}
          x2={shape.x2}
          y2={shape.y2}
          {...common}
        />
      );
    case 'rect':
      return (
        <rect
          key={key}
          x={shape.x}
          y={shape.y}
          width={shape.width}
          height={shape.height}
          rx={shape.rx}
          {...common}
        />
      );
    case 'circle':
      return (
        <circle key={key} cx={shape.cx} cy={shape.cy} r={shape.r} {...common} />
      );
    case 'path':
      return <path key={key} d={shape.d} {...common} />;
    case 'polyline':
      return (
        <polyline
          key={key}
          points={shape.points.map((point) => point.join(',')).join(' ')}
          {...common}
        />
      );
  }
};

/** Single-line diagram computed from the design's electrical topology. */
const ElectricalDiagram = ({
  region,
  topology,
  panels,
  faces,
  excludedFaceIds,
  stringColours,
  appliances,
  panelAppliances,
  battery,
  siteMinTemperatureC = null,
}: Props) => {
  const diagram = useMemo(
    () =>
      buildSolarSingleLineDiagram({
        region,
        topology,
        panels,
        roofs: faces.map((face, index) => ({
          faceId: face.id,
          label: `Roof ${index + 1} ${compassLabel(face.azimuthDegrees)}`,
        })),
        excludedFaceIds,
        products: [
          ...appliances,
          ...panelAppliances,
          ...(battery ? [battery] : []),
        ].map(product),
        stringColours,
        battery: battery
          ? { product: product(battery), quantity: battery.quantity }
          : null,
        siteMinTemperatureC,
      }),
    [
      region,
      topology,
      panels,
      faces,
      excludedFaceIds,
      stringColours,
      appliances,
      panelAppliances,
      battery,
      siteMinTemperatureC,
    ]
  );

  const download = () => {
    const blob = new Blob([solarSingleLineDiagramSvg(diagram)], {
      type: 'image/svg+xml',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'single-line-diagram.svg';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const [isOpen, setIsOpen] = useState(false);
  const svg = (fit: boolean) => (
    <svg
      role="img"
      aria-label={diagram.summary}
      width={fit ? '100%' : diagram.width}
      height={fit ? undefined : diagram.height}
      viewBox={`0 0 ${diagram.width} ${diagram.height}`}
      preserveAspectRatio="xMidYMid meet"
      className={fit ? 'block h-auto w-full' : 'block max-w-none'}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {diagram.shapes.map(shapeElement)}
    </svg>
  );
  const frame = 'rounded-lg border border-gray-200 bg-white text-gray-800';

  return (
    <section aria-labelledby="solar-diagram-title" className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <H4 id="solar-diagram-title">Diagram</H4>
        <Button
          variant="secondary"
          size="sm"
          disabled={!diagram.nodes.length}
          onClick={download}
        >
          Download
        </Button>
      </div>
      {diagram.nodes.length ? (
        <button
          type="button"
          aria-label="Open the diagram full size"
          className={`group relative block w-full cursor-pointer text-left ${frame}`}
          onClick={() => setIsOpen(true)}
        >
          {svg(true)}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          >
            <span className="rounded-full bg-white p-2 shadow-md">
              <UntitledIcon name="expand-06.3" className="size-5" />
            </span>
          </span>
        </button>
      ) : (
        <Text level="supporting">No inverters or strings yet</Text>
      )}
      <Modal
        title="Diagram"
        size="xl"
        isOpen={isOpen && !!diagram.nodes.length}
        onClose={() => setIsOpen(false)}
        disableBackdropClick={false}
      >
        <Modal.Body>
          <div className={`overflow-auto ${frame}`}>{svg(false)}</div>
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default ElectricalDiagram;
